import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Container, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { FormattedMessage } from "react-intl";
import Pagination from "@mui/lab/Pagination";
import sh from "bundles/common/utils/sh";
import CategoryTabs from "bundles/common/components/blog/CategoryTabs";
import FeaturedArticle from "bundles/common/components/blog/FeaturedArticle";
import ArticleCard from "bundles/common/components/blog/ArticleCard";
import BlogSidebar from "bundles/common/components/blog/BlogSidebar";
import BackdropScreen from "bundles/common/components/BackdropScreen";
import ArticleSvg from "articles.svg";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    backgroundColor: "#FCFCFC",
  },
  title: {
    marginBottom: "40px !important",
    fontSize: "2.25rem !important",
    color: "hsl(220 40% 13%)",
    [theme.breakpoints.up("md")]: {
      fontSize: "3rem !important",
      fontFamily: "Segoe UI Bold, system-ui, sans-serif !important",
    },
  },
  grid: {
    display: "grid",
    gap: 40,
    gridTemplateColumns: "1fr 320px",
    [theme.breakpoints.down("lg")]: {
      gridTemplateColumns: "1fr",
    },
  },
  sectionTitle: {
    marginBottom: "1.5rem !important",
    fontSize: "1.5rem !important",
    color: "hsl(220 40% 13%)",
    fontFamily: "Segoe UI Semibold, system-ui, sans-serif !important",
  },
  articleGrid: {
    display: "grid",
    gap: 24,
    gridTemplateColumns: "repeat(2, 1fr)",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  emptyState: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    marginTop: 120,
  },
  pagination: {
    justifyContent: "center",
    display: "flex",
    marginTop: 32,
  },
}));

const Blog = ({ type = "candidate" }) => {
  const classes = useStyles();
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const [activeType, setActiveType] = useState(type);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [articles, setArticles] = useState([]);
  const [pageCount, setPageCount] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [squarehubId, setSquarehubId] = useState(null);

  const validLocales = ["fr", "nl", "en"];
  const localeFilter = validLocales.includes(i18nLocale)
    ? { preferred_locale: i18nLocale }
    : {};

  const fetchArticles = () => {
    setLoading(true);
    const categoryFilter =
      selectedCategory !== "all" ? { category: selectedCategory } : {};

    sh.get("search/blog_posts", {
      authenticity_token: authenticityToken,
      params: {
        page: page,
        filters: {
          resource_type: activeType,
          ...localeFilter,
          ...categoryFilter,
        },
      },
    })
      .then((res) => {
        if (res?.data) {
          setArticles(res.data.data?.blog_posts);
          setPageCount(res.data.pagy?.pages);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setLoading(false));
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPage(1);
  };

  useEffect(() => {
    fetchArticles();
  }, [activeType, selectedCategory, page, i18nLocale]);

  // TODO: use slugs instead of ids for better SEO and cleaner URLs.
  useEffect(() => {
    sh.get(`/${i18nLocale}/find_squarehub_id`)
      .then((response) => {
        if (response?.data?.id) setSquarehubId(response.data.id);
      })
      .catch((err) => console.error(err));
  }, [i18nLocale]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [page]);

  const featured = page === 1 && articles?.length > 0 ? articles[0] : null;
  const rest =
    page === 1 && articles?.length > 1 ? articles.slice(1) : articles;

  return (
    <Box className={classes.root}>
      <CategoryTabs
        active={selectedCategory}
        onChange={handleCategoryChange}
        activeType={activeType}
      />

      <Container sx={{ py: 5, px: "3 !important", maxWidth: "1400px !important" }}>
        <Typography className={classes.title}>
          <FormattedMessage id="menu.blog_posts" />
        </Typography>

        <BackdropScreen open={loading} />

        {articles?.length > 0 && !loading && (
          <Box className={classes.grid}>
            <Box>
              {featured && (
                <FeaturedArticle post={featured} companyId={squarehubId} />
              )}
              {rest?.length > 0 && (
                <Box sx={{ mt: featured ? 7 : 0 }}>
                  <Typography className={classes.sectionTitle}>
                    <FormattedMessage id="blog.most_recent" />
                  </Typography>
                  <Box className={classes.articleGrid}>
                    {rest.map((post) => (
                      <ArticleCard
                        key={post.id}
                        post={post}
                        companyId={squarehubId}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {pageCount > 1 && (
                <Pagination
                  size="large"
                  count={pageCount}
                  page={page}
                  onChange={(e, newPage) => setPage(newPage)}
                  variant="outlined"
                  color="secondary"
                  className={classes.pagination}
                />
              )}
            </Box>

            <BlogSidebar />
          </Box>
        )}

        {articles?.length <= 0 && !loading && (
          <Box className={classes.emptyState}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6">
                <FormattedMessage id="blog.no_articles" />
              </Typography>
            </Box>
            <img src={ArticleSvg} style={{ width: 400 }} alt="" />
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Blog;
